import { useEffect, useRef } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';
import { locationService } from '../services/locationService';

// ═══════════════════════════════════════════════════════════════════════════
// EL VIGILANTE DEL ESTADO DE LA APP
// ═══════════════════════════════════════════════════════════════════════════
//
// EL FALLO QUE ESTO CIERRA
// Hasta hoy no había en todo el proyecto UNA SOLA llamada a
// `AppState.addEventListener`: la app nunca se enteraba de que el chofer la
// había minimizado. El vigilante de GPS seguía instalado, y por cada posición
// que Android le dejara colar escribía DOS documentos en Firestore. Nada de
// eso lanza una excepción de JavaScript, así que no hay stack trace: el
// sistema simplemente decide que esta app consume demasiado y mata el proceso.
// El chofer lo cuenta como "se cerró sola", y Google Play lo rechazó con esas
// mismas palabras ("apps que crashean o se congelan").
//
// LO QUE NO SE PIERDE AL SOLTAR EL GPS
// Con `UBICACION_SEGUNDO_PLANO = false` la app NO tiene el permiso de
// ubicación en segundo plano —decisión del fundador hasta pasar la revisión—,
// así que Android deja de entregar posiciones en cuanto la app deja de estar
// delante. El vigilante minimizado no estaba manteniendo vivo al chofer en el
// mapa: estaba gastando memoria y batería sin producir posiciones. Soltarlo no
// le quita al chofer nada que el sistema le estuviera dando.
//
// ═══ EL CICLO COMPLETO, PASO A PASO (probado mentalmente, que es lo que hay
//     hasta que esto corra en el teléfono del fundador) ═══
//
//  1. Chofer toca "Conectarse" → `startTracking` → vigilante IDLE instalado,
//     `modoDeRastreoActual()` devuelve 'IDLE'.
//  2. Acepta un viaje → `setTrackingMode('ACTIVE_RIDE')` → el modo deseado
//     pasa a 'ACTIVE_RIDE' y el vigilante se reinstala con precisión alta.
//  3. Minimiza (abre Waze, contesta WhatsApp) → AppState = 'background'.
//     · Si el modo es IDLE (nadie a bordo): se suelta EN EL ACTO.
//     · Si el modo es ACTIVE_RIDE: arranca la gracia de 45 s y NO se suelta
//       todavía (ver más abajo el porqué).
//  4. Vuelve antes de los 45 s → AppState = 'active' → se cancela la gracia y
//     no se soltó nada: el GPS nunca se interrumpió. Este es el caso más
//     frecuente de la calle y por eso existe la gracia.
//  5. Vuelve DESPUÉS (o venía de IDLE) → 'active' → `reanudarDesdeSegundoPlano`
//     lee el `driverId` que sigue guardado y el modo que se apuntó, comprueba
//     que el permiso sigue concedido y reinstala el vigilante con EL MISMO
//     modo que tenía. El chofer vuelve a emitir sin tocar nada.
//  6. Si mientras estaba fuera se desconectó (o `stopTracking` corrió por
//     cualquier vía), ya no hay `driverId`: no se reanuda nada. No se resucita
//     un GPS que nadie pidió.
//  7. Si la reanudación falla (permiso revocado en ajustes, GPS apagado), el
//     servicio se queda marcado como suspendido: se reintenta una vez a los
//     3 s y, si tampoco, en el siguiente regreso al primer plano. Un chofer
//     sin vigilante desaparece del mapa del pasajero, así que aquí NO se
//     abandona a la primera.
//
// ═══ POR QUÉ UN VIAJE ACTIVO NO SE SUELTA DE INMEDIATO ═══
// Con un pasajero a bordo, el marcador del chofer es lo único que el pasajero
// tiene para saber que su viaje existe. Un chofer manejando cambia de app
// constantemente —Waze, la llamada del pasajero, el mensaje de la central— y
// esos saltos duran segundos. Soltar el GPS en cada uno haría parpadear el
// marcador todo el viaje. La gracia de 45 s cubre el salto corto sin coste: si
// el chofer vuelve, no se interrumpió nada; si no vuelve, es que la app se
// quedó atrás de verdad y entonces sí se suelta, porque a partir de ahí
// Android tampoco iba a entregar posiciones. Lo único que se prolonga 45 s es
// el consumo, no un riesgo de que lo maten: lo que mata el proceso es la
// acumulación de horas, no tres cuartos de minuto.
// ═══════════════════════════════════════════════════════════════════════════

/** Cuánto se aguanta el GPS con un viaje en curso antes de soltarlo. */
const GRACIA_VIAJE_ACTIVO_MS = 45000;

/** Espera antes del único reintento de reanudación. */
const REINTENTO_REANUDAR_MS = 3000;

/**
 * Escucha el estado de la aplicación y suelta / restablece el vigilante de
 * GPS. Se monta UNA sola vez, en el layout raíz: es estado de proceso, no de
 * pantalla, y montarlo dos veces haría que dos oyentes pelearan por el mismo
 * vigilante.
 */
export function useAppStateGuard(): void {
  const temporizadorGraciaRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const temporizadorReintentoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const estadoAnteriorRef = useRef<AppStateStatus>(AppState.currentState);

  useEffect(() => {
    // En web no hay proceso que Android mate ni vigilante nativo que soltar.
    if (Platform.OS === 'web') return;

    const cancelarTemporizadores = () => {
      if (temporizadorGraciaRef.current) {
        clearTimeout(temporizadorGraciaRef.current);
        temporizadorGraciaRef.current = null;
      }
      if (temporizadorReintentoRef.current) {
        clearTimeout(temporizadorReintentoRef.current);
        temporizadorReintentoRef.current = null;
      }
    };

    const soltarVigilante = () => {
      temporizadorGraciaRef.current = null;
      locationService.suspenderPorSegundoPlano().catch((e: any) => {
        console.warn('[useAppStateGuard] no se pudo soltar el GPS:', e?.message);
      });
    };

    const reanudarVigilante = (esReintento = false) => {
      locationService
        .reanudarDesdeSegundoPlano()
        .then((resultado) => {
          // Solo se reintenta el FALLO. 'NADA_QUE_HACER' es la respuesta normal
          // de un pasajero o de un chofer desconectado: insistir ahí sería
          // consultar AsyncStorage cada 3 s para nada.
          if (resultado !== 'FALLO' || esReintento) return;
          if (AppState.currentState !== 'active') return;
          temporizadorReintentoRef.current = setTimeout(() => {
            temporizadorReintentoRef.current = null;
            if (AppState.currentState === 'active') reanudarVigilante(true);
          }, REINTENTO_REANUDAR_MS);
        })
        .catch((e: any) => {
          console.warn('[useAppStateGuard] no se pudo reanudar el GPS:', e?.message);
        });
    };

    const alCambiarEstado = (estado: AppStateStatus) => {
      const anterior = estadoAnteriorRef.current;
      estadoAnteriorRef.current = estado;

      if (estado === 'active') {
        // Volver cancela la gracia: si el chofer regresó dentro de los 45 s no
        // se llegó a soltar nada y no hay que reinstalar nada.
        cancelarTemporizadores();
        reanudarVigilante();
        return;
      }

      // 'inactive' es un parpadeo de iOS (centro de notificaciones, llamada
      // entrante): no es irse de la app y no dispara nada.
      if (estado !== 'background') return;
      // Android puede repetir 'background'; una sola vez basta.
      if (anterior === 'background') return;

      const modo = locationService.modoDeRastreoActual();
      // null = pasajero o chofer desconectado: no hay vigilante que soltar.
      if (modo === null) return;

      if (modo === 'ACTIVE_RIDE') {
        if (temporizadorGraciaRef.current) return;
        temporizadorGraciaRef.current = setTimeout(soltarVigilante, GRACIA_VIAJE_ACTIVO_MS);
        return;
      }
      soltarVigilante();
    };

    const suscripcion = AppState.addEventListener('change', alCambiarEstado);
    return () => {
      cancelarTemporizadores();
      try { suscripcion.remove(); } catch {}
    };
  }, []);
}

export default useAppStateGuard;
